import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonProps } from '@mui/material'
import { SettingsSuggest } from '@mui/icons-material'
import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import { usePolicy } from '../hooks'
import useDialog from '../hooks/useDialog'
import AlertTemplateDefaultsDialog from './AlertTemplateDefaultsDialog'
import ResponsiveButton from './ResponsiveButton'

export interface AlertTemplateDefaultsButtonProps
  extends Omit<ButtonProps, 'onClick' | 'disabled' | 'children'> {
  alertTemplates: AlertTemplate[]
  alertRules: AlertGroup[]
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  beforeUpdate?: (alertRules: AlertGroup[]) => void
  afterUpdate?: (alertRules: AlertGroup[]) => void
}

export default function AlertTemplateDefaultsButton({
  alertTemplates,
  alertRules,
  project,
  device,
  ruuviTag,
  variant = 'text',
  color = 'inherit',
  useRedux = true,
  beforeUpdate,
  afterUpdate,
  ...props
}: AlertTemplateDefaultsButtonProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The policy state.
   */
  const { canViewProjectAlertRules, canViewDeviceAlertRules, canViewRuuviTagAlertRules } =
    usePolicy()

  /**
   * Indicates if the current user can view alert rules of the project.
   */
  const canViewAlertRules =
    (project && canViewProjectAlertRules(project)) ||
    (device && canViewDeviceAlertRules(device)) ||
    (ruuviTag && canViewRuuviTagAlertRules(ruuviTag))

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Indicates if the alert rules are updating.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle before submit.
   */
  function handleBeforeSubmit() {
    beforeUpdate?.(alertRules)
    setSubmitting(true)
  }

  /**
   * Handle after submit.
   */
  function handleAfterSubmit() {
    afterUpdate?.(alertRules)

    setSubmitting(false)
    closeDialog()
  }

  /**
   * Handle close.
   */
  function handleClose() {
    setSubmitting(false)
    closeDialog()
  }

  return (
    <>
      {canViewAlertRules && (
        <ResponsiveButton
          variant={variant}
          color={color}
          label={t('showDefaultValues')}
          icon={<SettingsSuggest />}
          disabled={isSubmitting}
          onClick={openDialog}
          {...props}
        />
      )}

      <AlertTemplateDefaultsDialog
        alertTemplates={alertTemplates}
        alertRules={alertRules}
        project={project}
        device={device}
        ruuviTag={ruuviTag}
        open={open}
        useRedux={useRedux}
        beforeSubmit={handleBeforeSubmit}
        afterSubmit={handleAfterSubmit}
        onClose={handleClose}
      />
    </>
  )
}
